import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = {
  // Generic methods for direct API calls
  get: (path, config) => axios.get(`${API}${path}`, config),
  post: (path, data, config) => axios.post(`${API}${path}`, data, config),
  put: (path, data, config) => axios.put(`${API}${path}`, data, config),
  delete: (path, config) => axios.delete(`${API}${path}`, config),

  // Assets
  getAssets: (category) => axios.get(`${API}/assets`, { params: { category } }),
  getAsset: (id) => axios.get(`${API}/assets/${id}`),
  createAsset: (data) => axios.post(`${API}/assets`, data),
  updateAsset: (id, data) => axios.put(`${API}/assets/${id}`, data),
  deleteAsset: (id) => axios.delete(`${API}/assets/${id}`),

  // Asset Valuation
  getAssetValuation: (id) => axios.get(`${API}/assets/${id}/valuation`),

  // Portfolio
  getPortfolioSummary: () => axios.get(`${API}/portfolio/summary`),
  getPortfolioPerformance: () => axios.get(`${API}/portfolio/performance`),
  getPortfolioHistory: (range) => axios.get(`${API}/portfolio/history`, { params: { range } }),

  // Market Indices
  getMarketIndices: (category) => axios.get(`${API}/market/indices`, { params: { category } }),

  // Price Alerts
  getAlerts: () => axios.get(`${API}/alerts`),
  createAlert: (data) => axios.post(`${API}/alerts`, data),
  deleteAlert: (id) => axios.delete(`${API}/alerts/${id}`),

  // Subscriptions
  getSubscriptionPlans: () => axios.get(`${API}/subscriptions/plans`),
  getSubscriptionAccess: () => axios.get(`${API}/subscriptions/access`),
  getSubscriptionUsage: () => axios.get(`${API}/subscriptions/usage`),
  getSubscriptionDetails: () => axios.get(`${API}/subscriptions/details`),
  getBillingHistory: (limit = 20) => axios.get(`${API}/subscriptions/billing-history`, { params: { limit } }),
  cancelSubscription: () => axios.post(`${API}/subscriptions/cancel`),
  reactivateSubscription: () => axios.post(`${API}/subscriptions/reactivate`),
  downgradeSubscription: () => axios.post(`${API}/subscriptions/downgrade`),
  upgradeSubscription: (tier) => axios.post(`${API}/subscriptions/upgrade`, { tier }),
  
  // Stripe Checkout
  createCheckoutSession: (tier, billingCycle) => axios.post(`${API}/subscriptions/checkout`, {
    tier,
    billing_cycle: billingCycle,
    origin_url: window.location.origin
  }),
  getCheckoutStatus: (sessionId) => axios.get(`${API}/subscriptions/checkout/status/${sessionId}`),

  // Maintenance
  getMaintenance: (assetId) => axios.get(`${API}/maintenance`, { params: { asset_id: assetId } }),
  createMaintenance: (data) => axios.post(`${API}/maintenance`, data),
  updateMaintenance: (id, status) => axios.put(`${API}/maintenance/${id}`, null, { params: { status } }),
  deleteMaintenance: (id) => axios.delete(`${API}/maintenance/${id}`),

  // Documents
  getDocuments: (assetId) => axios.get(`${API}/documents`, { params: { asset_id: assetId } }),
  getVault: () => axios.get(`${API}/documents/vault`),
  uploadDocument: (formData) => axios.post(`${API}/documents/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDocumentDownloadUrl: (docId) => `${API}/documents/${docId}/download`,
  createDocument: (data) => axios.post(`${API}/documents`, data),
  deleteDocument: (id) => axios.delete(`${API}/documents/${id}`),

  // Opportunities
  getOpportunities: (category) => axios.get(`${API}/opportunities`, { params: { category } }),

  // AI Insights
  getQuickPrompts: () => axios.get(`${API}/insights/quick-prompts`),
  getInsights: (query) => axios.post(`${API}/insights`, { query }),

  // AI Asset Valuation
  getAIValuation: (data) => axios.post(`${API}/assets/ai-valuation`, data),

  // Support Chat
  supportChat: (data) => axios.post(`${API}/support/chat`, data),

  // Security
  getSecuritySettings: () => axios.get(`${API}/security/settings`),
  setup2FA: () => axios.post(`${API}/security/2fa/setup`),
  verify2FA: (code) => axios.post(`${API}/security/2fa/verify`, { code }),
  disable2FA: (code) => axios.post(`${API}/security/2fa/disable`, { code }),
  getSessions: () => axios.get(`${API}/security/sessions`),
  revokeSession: (sessionId) => axios.delete(`${API}/security/sessions/${sessionId}`),
  revokeAllSessions: () => axios.delete(`${API}/security/sessions`),
  getActivityLog: (limit = 50) => axios.get(`${API}/security/activity`, { params: { limit } }),
  changePassword: (currentPassword, newPassword) => axios.post(`${API}/security/password/change`, {
    current_password: currentPassword,
    new_password: newPassword
  }),
  updateLoginNotifications: (enabled) => axios.put(`${API}/security/notifications`, null, { params: { enabled } }),
  exportPortfolio: (password, includeDocuments = false) => axios.post(`${API}/export/portfolio`, {
    password,
    include_documents: includeDocuments
  }, { responseType: password ? 'blob' : 'json' }),

  // Inquiries
  submitInquiry: (data) => axios.post(`${API}/inquiries`, data),
  getInquiries: () => axios.get(`${API}/inquiries`),
  getInquiry: (id) => axios.get(`${API}/inquiries/${id}`),

  // Valuations
  valuateAsset: (assetId) => axios.post(`${API}/valuations/valuate/${assetId}`),
  acceptAIValuation: (assetId) => axios.post(`${API}/valuations/accept/${assetId}`),
  getValuationHistory: (assetId, limit = 20) => axios.get(`${API}/valuations/history/${assetId}`, { params: { limit } }),
  valuatePortfolio: () => axios.post(`${API}/valuations/portfolio`),
  getPortfolioValuationSummary: () => axios.get(`${API}/valuations/portfolio/summary`),
  getValuationStatus: () => axios.get(`${API}/valuations/status`),
  triggerScheduledValuations: () => axios.post(`${API}/valuations/trigger-scheduled`),

  // Sell Assets
  getAllSellingPlatforms: () => axios.get(`${API}/sell/platforms`),
  getCategoryPlatforms: (category) => axios.get(`${API}/sell/platforms/${category}`),
  getRecommendedPlatforms: (assetId) => axios.get(`${API}/sell/recommended/${assetId}`),

  // Uploads
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API}/uploads/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadAssetImage: (assetId, formData) => {
    return axios.post(`${API}/uploads/asset-image/${assetId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // AI Insurance Document Scanner
  extractInsurance: (formData) => axios.post(`${API}/assets/extract-insurance`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),

  // Reports
  downloadPortfolioReport: () => {
    return axios.get(`${API}/reports/portfolio`, { responseType: 'blob' });
  },
  downloadAssetCertificate: (assetId) => {
    return axios.get(`${API}/reports/asset/${assetId}`, { responseType: 'blob' });
  },

  // Tokens / Blockchain
  getBlockchainStatus: () => axios.get(`${API}/tokens/status`),
  getWallet: () => axios.get(`${API}/tokens/wallet`),
  linkWallet: (data) => axios.post(`${API}/tokens/wallet/link`, data),
  tokenizeAsset: (data) => axios.post(`${API}/tokens/tokenize`, data),
  getMyTokens: () => axios.get(`${API}/tokens/my-tokens`),
  getToken: (id) => axios.get(`${API}/tokens/${id}`),
  listTokenForSale: (id) => axios.post(`${API}/tokens/${id}/list`),
  getSellerReadiness: () => axios.get(`${API}/tokens/seller-readiness`),

  // Token Re-valuation (AI-tracked appreciation) + Buyback (un-tokenisation)
  revalueToken: (id) => axios.post(`${API}/tokens/${id}/revalue`),
  getTokenPriceHistory: (id) => axios.get(`${API}/tokens/${id}/price-history`),
  getBuybackQuote: (id) => axios.get(`${API}/tokens/${id}/buyback-quote`),
  startBuyback: (id) => axios.post(`${API}/tokens/${id}/buyback/checkout`),
  cancelBuyback: (id) => axios.post(`${API}/tokens/${id}/buyback/cancel`),
  notifyHoldersToOnboard: (id) => axios.post(`${API}/tokens/${id}/buyback/notify-holders`),
  getBuybackStatus: (sessionId) => axios.get(`${API}/tokens/buyback/checkout-status/${sessionId}`),
  burnUnsoldToken: (id, body) => axios.post(`${API}/tokens/${id}/burn`, body || {}),

  // Marketplace - Price Alerts (per-token)
  createFractionAlert: (data) => axios.post(`${API}/marketplace/alerts`, data),
  listFractionAlerts: () => axios.get(`${API}/marketplace/alerts`),
  deleteFractionAlert: (id) => axios.delete(`${API}/marketplace/alerts/${id}`),

  // Marketplace - Custom Offers
  createOffer: (data) => axios.post(`${API}/marketplace/offers`, data),
  listOffers: (role = 'all') => axios.get(`${API}/marketplace/offers`, { params: { role } }),
  respondToOffer: (id, data) => axios.post(`${API}/marketplace/offers/${id}/respond`, data),
  withdrawOffer: (id) => axios.post(`${API}/marketplace/offers/${id}/withdraw`),
  createOfferCheckout: (id) => axios.post(`${API}/marketplace/offers/${id}/checkout`),

  // 3D Models
  generate3DModel: (assetId, imageUrl) => axios.post(`${API}/3d-models/generate`, { asset_id: assetId, image_url: imageUrl }),
  get3DModel: (assetId) => axios.get(`${API}/3d-models/${assetId}`),

  // Marketplace
  getMarketplaceListings: (category, sortBy) => axios.get(`${API}/marketplace/listings`, { params: { category: category || undefined, sort_by: sortBy } }),
  getMyListings: () => axios.get(`${API}/marketplace/my-listings`),
  getMarketplaceHoldings: () => axios.get(`${API}/marketplace/holdings`),
  getMarketplaceOrders: () => axios.get(`${API}/marketplace/orders`),
  getMarketplaceStats: () => axios.get(`${API}/marketplace/stats`),
  getMarketplaceActivity: () => axios.get(`${API}/marketplace/activity`),
  getHoldingsPerformance: () => axios.get(`${API}/marketplace/holdings-performance`),
  createMarketplaceOrder: (data) => axios.post(`${API}/marketplace/orders`, data),

  // Admin
  getAdminDashboard: () => axios.get(`${API}/admin/dashboard`),
  getVerificationQueue: (status) => axios.get(`${API}/admin/verification-queue`, { params: { status } }),
  getVerificationDetail: (id) => axios.get(`${API}/admin/verification/${id}`),
  approveVerification: (id) => axios.post(`${API}/admin/verification/${id}/approve`),
  rejectVerification: (id, data) => axios.post(`${API}/admin/verification/${id}/reject`, data || {}),
  getAdminUsers: () => axios.get(`${API}/admin/users`),
  getVerificationStatus: (assetId) => axios.get(`${API}/tokens/verification-status/${assetId}`),

  // Notifications
  getNotifications: () => axios.get(`${API}/notifications`),
  getUnreadCount: () => axios.get(`${API}/notifications/unread-count`),
  markNotificationRead: (id) => axios.post(`${API}/notifications/${id}/read`),
  markAllNotificationsRead: () => axios.post(`${API}/notifications/read-all`),

  // Digital Twin Access
  getDigitalTwinStatus: () => axios.get(`${API}/digital-twin-access/status`),
  requestDigitalTwinAccess: () => axios.post(`${API}/digital-twin-access/request`),
  createDigitalTwinCheckout: () => axios.post(`${API}/digital-twin-access/checkout`),
  activateDigitalTwinAccess: () => axios.post(`${API}/digital-twin-access/activate`),
  getDigitalTwinRequests: (status) => axios.get(`${API}/admin/digital-twin-requests`, { params: { status } }),
  approveDigitalTwinAccess: (userId) => axios.post(`${API}/admin/digital-twin-access/${userId}/approve`),
  rejectDigitalTwinAccess: (userId) => axios.post(`${API}/admin/digital-twin-access/${userId}/reject`),

  // Admin - Partners & Pipeline
  createPartner: (data) => axios.post(`${API}/admin/create-partner`, data),
  getPartners: () => axios.get(`${API}/admin/partners`),
  getTokenizationPipeline: () => axios.get(`${API}/admin/tokenization-pipeline`),
  inviteBroker: (data) => axios.post(`${API}/partners/invite-broker`, data),

  // Admin - Blockchain (Polygon Registry)
  getBlockchainRegistry: () => axios.get(`${API}/admin/blockchain/registry`),
  setRegistryContractAddress: (contract_address) =>
    axios.post(`${API}/admin/blockchain/set-contract-address`, { contract_address }),
  deployRegistryContract: () =>
    axios.post(`${API}/admin/blockchain/deploy-registry`, {}, { timeout: 240000 }),
  sendTestMintReceipt: () =>
    axios.post(`${API}/admin/blockchain/send-test-receipt`, {}),
  backfillRegistryMint: (tokenId) =>
    axios.post(`${API}/admin/blockchain/backfill-mint/${tokenId}`, {}, { timeout: 240000 }),

  // Messaging
  getMessageThreads: () => axios.get(`${API}/messaging/threads`),
  getThreadMessages: (threadId) => axios.get(`${API}/messaging/threads/${threadId}`),
  sendMessage: (data) => axios.post(`${API}/messaging/send`, data),
  getUnreadMessages: () => axios.get(`${API}/messaging/unread-count`),
  getSupportInbox: () => axios.get(`${API}/messaging/support-inbox`),
  sendSupportMessage: (data) => axios.post(`${API}/messaging/support`, data),

  // Partner Dashboard
  getPartnerDashboard: () => axios.get(`${API}/partners/dashboard`),
  getPartnerClients: () => axios.get(`${API}/partners/clients`),
  partnerVerify: (id, verified) => axios.post(`${API}/partners/verify/${id}`, null, { params: { verified, notes: '' } }),
  partnerRegisterClient: (data) => axios.post(`${API}/partners/register-client`, data),
  // Advisor Hub v3 - Partner connectivity
  getMyAdvisor: () => axios.get(`${API}/partners/my-advisor`),
  getPartnerInbox: () => axios.get(`${API}/partners/inbox`),

  // Marketplace - Stripe Purchase
  createPurchaseCheckout: (tokenId, fractions) => axios.post(`${API}/marketplace/create-checkout`, null, { params: { token_id: tokenId, fractions } }),
  getMarketplaceCheckoutStatus: (sessionId) => axios.get(`${API}/marketplace/checkout-status/${sessionId}`),

  // Stripe Connect (Seller Payouts)
  getConnectStatus: () => axios.get(`${API}/stripe-connect/account-status`),
  createConnectAccount: () => axios.post(`${API}/stripe-connect/create-account`),
  refreshConnectLink: () => axios.post(`${API}/stripe-connect/refresh-link`),
  connectLoginLink: () => axios.post(`${API}/stripe-connect/login-link`),

  // Partner client detail
  getClientOverview: (clientId) => axios.get(`${API}/partners/clients/${clientId}/overview`),

  // KYC / Investor Certification (UK FCA FPO)
  getKycStatus: () => axios.get(`${API}/kyc/status`),
  certifyKyc: (payload) => axios.post(`${API}/kyc/certify`, payload),
  revokeKyc: () => axios.post(`${API}/kyc/revoke`),
  adminListKyc: () => axios.get(`${API}/kyc/admin/all`),

  // Personal Profile (encrypted)
  getMyProfile: () => axios.get(`${API}/profile/me`),
  updateMyProfile: (payload) => axios.put(`${API}/profile/me`, payload),
  getProfileCompleteness: () => axios.get(`${API}/profile/completeness`),
  adminListProfileCompleteness: () => axios.get(`${API}/profile/admin/all`),

  // Risk analytics
  getRiskSummary: () => axios.get(`${API}/risk/summary`),
  updateRiskPreferences: (payload) => axios.put(`${API}/risk/preferences`, payload),

  // Timeline
  getTimelineFeed: () => axios.get(`${API}/timeline/feed`),

  // PII Share
  listShareableFields: () => axios.get(`${API}/pii-share/fields`),
  createPiiShare: (payload) => axios.post(`${API}/pii-share`, payload),
  listPiiShares: () => axios.get(`${API}/pii-share`),
  revokePiiShare: (id) => axios.delete(`${API}/pii-share/${id}`),

  // Action Tickets
  listTickets: (status) => axios.get(`${API}/action-tickets`, { params: status ? { status } : {} }),
  createTicket: (payload) => axios.post(`${API}/action-tickets`, payload),
  updateTicket: (id, payload) => axios.patch(`${API}/action-tickets/${id}`, payload),
  deleteTicket: (id) => axios.delete(`${API}/action-tickets/${id}`),
  getTicketThread: (id) => axios.get(`${API}/action-tickets/${id}/thread`),
  replyToTicket: (id, body) => axios.post(`${API}/action-tickets/${id}/reply`, { body }),

  // Verified Brokers
  verifyBroker: (email) => axios.get(`${API}/brokers/verify`, { params: { email } }),
  listBrokers: () => axios.get(`${API}/brokers`),
  createBroker: (payload) => axios.post(`${API}/brokers`, payload),
  updateBroker: (id, payload) => axios.patch(`${API}/brokers/${id}`, payload),
  deleteBroker: (id) => axios.delete(`${API}/brokers/${id}`),
  sendBrokerOnboarding: (id) => axios.post(`${API}/brokers/${id}/send-onboarding`),
  // Broker self-service applications (Phase 2)
  getBrokerBySlug: (slug) => axios.get(`${API}/brokers/by-slug/${slug}`),
  submitBrokerApplication: (formData) => axios.post(`${API}/brokers/applications`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listBrokerApplications: (status) => axios.get(`${API}/brokers/applications`, { params: status ? { status } : {} }),
  approveBrokerApplication: (id) => axios.post(`${API}/brokers/applications/${id}/approve`),
  rejectBrokerApplication: (id, reason) => axios.post(`${API}/brokers/applications/${id}/reject`, { reason: reason || null }),

  // GDPR
  exportMyData: () => axios.post(`${API}/profile/export`),
  deleteMyAccount: () => axios.delete(`${API}/profile/account`),
};
